import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { FaCalendarAlt } from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";
import { getLanguagePrefix } from "../routing/localizedPaths";

/**
 * Floating "Book en prat" CTA. Appears after the user scrolls roughly past
 * the hero (~480px) so it never competes with the in-hero primary CTA.
 *
 * Suppresses itself on /book and /kontakt to avoid noise on conversion
 * surfaces, and on routes the parent decides not to render it on.
 *
 * Also auto-hides when the footer enters the viewport so it does not
 * collide with footer links / contact rows on mobile.
 */
const SCROLL_TRIGGER_PX = 480;
// Distance (px) from the bottom of the document at which we hide the FAB
// to keep it from overlapping the footer. ~Footer height + safe margin.
const FOOTER_PROXIMITY_PX = 320;

const labels = {
  no: { cta: "Book en prat", aria: "Book en prat med VImplement" },
  en: { cta: "Book a call", aria: "Book a call with VImplement" },
};

export default function StickyBookCta() {
  const { language } = useLanguage();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  const prefix = getLanguagePrefix(language);
  const target = `${prefix}/book`;
  const t = labels[language] ?? labels.en;

  // Hide on conversion surfaces themselves.
  const path = location.pathname.replace(/\/$/, "");
  const isBookOrContact =
    path.endsWith("/book") || path.endsWith("/kontakt") || path.endsWith("/contact");

  useEffect(() => {
    if (isBookOrContact) {
      setVisible(false);
      return undefined;
    }
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const viewport = window.innerHeight;
      const distanceFromBottom = docHeight - (scrolled + viewport);
      const pastTrigger = scrolled > SCROLL_TRIGGER_PX;
      const nearFooter = distanceFromBottom < FOOTER_PROXIMITY_PX;
      setVisible(pastTrigger && !nearFooter);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isBookOrContact, location.pathname]);

  if (isBookOrContact) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        // Respect iOS safe-area-inset (notch / home indicator) so the FAB
        // doesn't sit under the home pill on modern iPhones.
        right: {
          xs: "calc(env(safe-area-inset-right, 0px) + 16px)",
          md: 28,
        },
        bottom: {
          xs: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          md: 28,
        },
        zIndex: 1200,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        pointerEvents: visible ? "auto" : "none",
        transition: "opacity .25s ease, transform .25s ease",
      }}
    >
      <Button
        component={Link}
        to={target}
        aria-label={t.aria}
        startIcon={<FaCalendarAlt aria-hidden="true" />}
        sx={{
          backgroundColor: "#0F172A",
          color: "white",
          fontWeight: 700,
          textTransform: "none",
          borderRadius: "999px",
          px: { xs: 2.25, md: 2.75 },
          py: { xs: 1.1, md: 1.25 },
          fontSize: { xs: "0.88rem", md: "0.92rem" },
          // Guarantee >=44px tap target on mobile per WCAG 2.5.5.
          minHeight: 44,
          boxShadow: "0 12px 28px -10px rgba(15,23,42,0.45)",
          "&:hover": {
            backgroundColor: "#1E293B",
            boxShadow: "0 14px 32px -10px rgba(15,23,42,0.55)",
          },
        }}
      >
        {t.cta}
      </Button>
    </Box>
  );
}
